import React, { useState, useEffect } from 'react';
import { useAdmin } from '../context/AdminContext';

const ContactManagement = ({ setMessage }) => {
    const { getContactInfo, updateContactInfo } = useAdmin();
    const [contactInfo, setContactInfo] = useState({
        address: '',
        phone: '',
        email: ''
    });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        loadContactInfo();
    }, []);

    const loadContactInfo = async () => {
        try {
            setLoading(true);
            const data = await getContactInfo();
            setContactInfo(data);
            setMessage('Contact information loaded successfully');
        } catch (error) {
            console.error('Failed to load contact info:', error);
            setMessage('Error loading contact information');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setContactInfo(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            const result = await updateContactInfo(contactInfo);
            setMessage(result.message || 'Contact information updated successfully!');

            // Reload to get updated data
            await loadContactInfo();
        } catch (error) {
            console.error('Failed to update contact info:', error);
            setMessage('Error updating contact information: ' + error.message);
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        loadContactInfo();
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading contact information...</p>
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0">
                    <i className="fas fa-address-book text-primary me-2"></i>
                    Contact Information Management
                </h4>
            </div>

            <div className="row">
                <div className="col-lg-8">
                    <div className="card">
                        <div className="card-header bg-white">
                            <h5 className="mb-0">Edit Contact Information</h5>
                            <p className="text-muted mb-0 small">
                                Update the contact information that appears in the website footer
                            </p>
                        </div>
                        <div className="card-body">
                            <form onSubmit={handleSubmit}>
                                <div className="mb-3">
                                    <label htmlFor="address" className="form-label">
                                        <i className="fas fa-map-marker-alt me-2 text-muted"></i>
                                        Address *
                                    </label>
                                    <textarea
                                        id="address"
                                        name="address"
                                        className="form-control"
                                        rows="3"
                                        value={contactInfo.address}
                                        onChange={handleChange}
                                        placeholder="Enter company address"
                                        required
                                    />
                                    <div className="form-text">
                                        This address will be displayed in the footer
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label htmlFor="phone" className="form-label">
                                        <i className="fas fa-phone-alt me-2 text-muted"></i>
                                        Phone Number *
                                    </label>
                                    <input
                                        type="text"
                                        id="phone"
                                        name="phone"
                                        className="form-control"
                                        value={contactInfo.phone}
                                        onChange={handleChange}
                                        placeholder="Enter phone number"
                                        required
                                    />
                                    <div className="form-text">
                                        Format: +012 345 67890
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <label htmlFor="email" className="form-label">
                                        <i className="fas fa-envelope me-2 text-muted"></i>
                                        Email Address *
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        className="form-control"
                                        value={contactInfo.email}
                                        onChange={handleChange}
                                        placeholder="Enter email address"
                                        required
                                    />
                                    <div className="form-text">
                                        This email will be used for contact purposes
                                    </div>
                                </div>

                                <div className="d-flex gap-2">
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={saving}
                                    >
                                        {saving ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" />
                                                Updating...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-save me-2"></i>
                                                Update Contact Info
                                            </>
                                        )}
                                    </button>

                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={handleReset}
                                        disabled={saving}
                                    >
                                        <i className="fas fa-sync me-2"></i>
                                        Reset Changes
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                <div className="col-lg-4">
                    <div className="card">
                        <div className="card-header bg-white">
                            <h5 className="mb-0">Live Preview</h5>
                            <p className="text-muted mb-0 small">
                                How it will appear in the footer
                            </p>
                        </div>
                        <div className="card-body">
                            <div className="border rounded p-3 bg-light">
                                <h6 className="text-dark mb-3">Contact</h6>
                                <p className="mb-2">
                                    <i className="fa fa-map-marker-alt me-3 text-primary"></i>
                                    {contactInfo.address || 'No address set'}
                                </p>
                                <p className="mb-2">
                                    <i className="fa fa-phone-alt me-3 text-primary"></i>
                                    {contactInfo.phone || 'No phone set'}
                                </p>
                                <p className="mb-0">
                                    <i className="fa fa-envelope me-3 text-primary"></i>
                                    {contactInfo.email || 'No email set'}
                                </p>
                            </div>

                            <div className="mt-3">
                                <div className="alert alert-info small">
                                    <i className="fas fa-info-circle me-2"></i>
                                    <strong>Note:</strong> Changes will be immediately visible on the website footer.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContactManagement;